import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";
import { Button } from "./Button";

interface SignaturePadProps {
  onConfirm: (file: File) => void;
  onCancel: () => void;
  /** Nome del firmatario, mostrato come didascalia — puramente informativo. */
  label?: string;
}

/**
 * Pannello di firma touch: disegna con dito/penna/mouse su un canvas HTML5,
 * esporta il risultato come File PNG pronto per essere caricato con lo
 * stesso sistema media usato per foto/video (vedi api/media.ts) — nessuna
 * libreria esterna, gli eventi Pointer unificano mouse/touch/penna.
 */
export function SignaturePad({ onConfirm, onCancel, label }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Ridimensiona il canvas alla risoluzione reale del device (altrimenti su
  // schermi ad alta densità la firma appare sfocata/frastagliata).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function resize() {
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#18273d";
      }
    }

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  function getPoint(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;
    lastPointRef.current = getPoint(e);
    setHasDrawn(true);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !lastPointRef.current) return;

    const point = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
  }

  function handlePointerUp() {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }

  function handleConfirm() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `firma-${Date.now()}.png`, { type: "image/png" });
      onConfirm(file);
    }, "image/png");
  }

  return (
    <div className="flex flex-col gap-3">
      {label && <p className="text-sm text-ink/60">{label}</p>}

      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="h-48 w-full touch-none rounded-lg border-2 border-dashed border-black/20 bg-white"
      />

      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="sm" onClick={handleClear} disabled={!hasDrawn}>
          <Eraser className="h-3.5 w-3.5" aria-hidden="true" />
          Cancella
        </Button>

        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annulla
          </Button>
          <Button type="button" disabled={!hasDrawn} onClick={handleConfirm}>
            Conferma firma
          </Button>
        </div>
      </div>
    </div>
  );
}
